import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { Observable, firstValueFrom, from, map, switchMap } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import {
  AiAssistAppliedEvent,
  AiAssistDiscardFn,
  AiAssistGenerateFn,
  AiAssistGenerateRequest,
  AiAssistResult
} from '../../shared/ai-assist/ai-assist.models';
import { hasAiAssistPermission } from '../../shared/ai-assist/ai-assist.util';
import { AinAiAssistShellComponent } from '../../shared/ai-assist/ain-ai-assist-shell.component';
import { KnowledgeSourceSelectorComponent } from '../../shared/design-system/knowledge-source-selector.component';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { StructuredJsonFieldDef } from '../../shared/design-system/structured-json-form.model';
import {
  CatalogService,
  StorefrontContentProfile,
  StorefrontProfileFaqItem,
  StorefrontProfileHighlight,
  StorefrontProfileLink
} from '../catalog/catalog.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { WizardApiService, WizardRunDto } from './wizard-api.service';
import { GrowthContentAssetEditorComponent } from '../growth/growth-content-asset-editor.component';
import {
  GrowthContentAssetEditorState,
  buildGrowthArticlePublishContentStepPayload,
  createDefaultGrowthContentAssetEditorState,
  patchGrowthContentAssetEditorFromWizardContext
} from '../growth/growth-content-asset-editor.util';
import {
  applyGrowthContentAssistFieldPatches,
  buildGrowthContentAssistClientSnapshot,
  computeGrowthContentAssistSourceHash,
  mapGrowthContentAssistDraftToFieldPatches
} from '../growth/growth-content-asset-ai-assist.util';
import { GrowthMarketingService } from '../growth/growth-marketing.service';
import { GrowthDistributionService } from '../growth/growth-distribution.service';
import {
  buildGrowthArticleOptionalPublishChannelOptions,
  GROWTH_ARTICLE_OPTIONAL_PUBLISH_CHANNELS
} from '../growth/growth-channel.constants';
import { GROWTH_ARTICLE_PUBLISH_CHANNEL } from '../growth/growth-article-publishing.util';

const GROWTH_CONTENT_AI_ASSIST_PERMISSION = 'growth.content.ai.assist';

interface ChecklistItem {
  key: string;
  status: string;
  error: string | null;
}

interface CatalogProductRow {
  title: string;
  shortDescription: string;
  categoryName: string;
  basePrice: number | null;
}

interface SelectOption {
  label: string;
  value: string | null;
}

interface ProfileHighlightRow {
  title: string;
  body: string;
}

interface ProfileFaqRow {
  question: string;
  answer: string;
  category: string;
}

interface ProfileLinkRow {
  label: string;
  url: string;
}

@Component({
  standalone: true,
  selector: 'ain-wizard-run-host',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    GrowthContentAssetEditorComponent,
    FormsModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    InputNumberModule,
    CheckboxModule,
    SelectModule,
    DialogModule,
    ToastModule,
    AinAiAssistShellComponent,
    AinFormFieldComponent,
    KnowledgeSourceSelectorComponent
  ],
  providers: [MessageService],
  template: `
    <p-toast />
    <ain-page
      [eyebrow]="tw('eyebrow')"
      [title]="tw('title')"
      [description]="tw('description')">
      <ain-info-hint
        [titleKey]="hintTitleKey()"
        [descriptionKey]="hintDescriptionKey()"
        [bulletKeys]="hintBulletKeys()" />

      @if (loading()) {
        <ain-state type="loading" [title]="tw('loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="tw('errorTitle')" [message]="error() ?? ''" />
      } @else if (run()) {
        <section class="checklist-panel">
          <h3>{{ tw('progressTitle') }}</h3>
          <ol class="checklist">
            @for (step of checklist(); track step.key) {
              <li [class]="stepClass(step)">
                <span class="mark" [attr.aria-label]="stepStatusLabel(step.status)">
                  @switch (step.status) {
                    @case ('Succeeded') { <i class="pi pi-check" aria-hidden="true"></i> }
                    @case ('Skipped') { <i class="pi pi-minus" aria-hidden="true"></i> }
                    @case ('Failed') { <i class="pi pi-times" aria-hidden="true"></i> }
                    @case ('AwaitingApproval') { <i class="pi pi-clock" aria-hidden="true"></i> }
                    @case ('Pending') {
                      @if (step.key === run()!.currentStepKey) {
                        <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
                      } @else {
                        <i class="pi pi-circle" aria-hidden="true"></i>
                      }
                    }
                    @default { <i class="pi pi-circle" aria-hidden="true"></i> }
                  }
                </span>
                <div class="step-body">
                  <strong>{{ stepLabel(step.key) }}</strong>
                  @if (step.error) {
                    <p class="step-error">{{ wizardErrorLabel(step.error) }}</p>
                  }
                </div>
              </li>
            }
          </ol>
        </section>

        @if (run()!.status === 'Completed') {
          <section class="success-panel">
            <p>{{ tw('completedMessage') }}</p>
            <button pButton type="button" [label]="completionButtonLabel()" (click)="goToCompletion()"></button>
          </section>
        } @else if (currentStepKey()) {
          <section class="step-panel">
            <h3>{{ stepLabel(currentStepKey()!) }}</h3>
            @if (showCurrentStepForm()) {
              @if (isGrowthWizard()) {
                @switch (currentStepKey()) {
                  @case ('welcome') {
                    <p>{{ tw('steps.welcome.intro') }}</p>
                    @if (runContext('title') !== '—') {
                      <dl class="summary-grid">
                        <div>
                          <dt>{{ tw('steps.welcome.fieldTitle') }}</dt>
                          <dd>{{ runContext('title') }}</dd>
                        </div>
                        <div>
                          <dt>{{ tw('steps.welcome.fieldSlug') }}</dt>
                          <dd>{{ runContext('publicSlug') }}</dd>
                        </div>
                        <div>
                          <dt>{{ tw('steps.welcome.fieldLocale') }}</dt>
                          <dd>{{ runContext('locale') }}</dd>
                        </div>
                        <div>
                          <dt>{{ tw('steps.welcome.fieldStatus') }}</dt>
                          <dd>{{ runContext('contentStatus') }}</dd>
                        </div>
                      </dl>
                    }
                  }
                  @case ('content') {
                    <ain-info-hint
                      [titleKey]="'growth.contentAsset.help.page.title'"
                      [descriptionKey]="'growth.contentAsset.help.page.description'"
                      [bulletKeys]="[
                        'growth.contentAsset.help.page.bullets.basics',
                        'growth.contentAsset.help.page.bullets.body',
                        'growth.contentAsset.help.page.bullets.seo',
                        'growth.contentAsset.help.page.bullets.citations',
                        'growth.contentAsset.help.page.bullets.publishTargets'
                      ]" />
                    @if (canUseGrowthContentAiAssist()) {
                      <div class="content-ai-toolbar">
                        <button
                          pButton
                          type="button"
                          icon="pi pi-sparkles"
                          severity="secondary"
                          [label]="growthContentAiAssistMode() === 'create' ? t('growth.contentAsset.aiAssist.actions.generate') : t('growth.contentAsset.aiAssist.actions.edit')"
                          [disabled]="submitting()"
                          (click)="openGrowthContentAiAssist()"></button>
                        @if (hasGrowthContentBodyForImprove()) {
                          <button
                            pButton
                            type="button"
                            icon="pi pi-verified"
                            severity="help"
                            [outlined]="true"
                            [label]="t('growth.contentAsset.aiAssist.actions.improve')"
                            [disabled]="submitting()"
                            (click)="openGrowthContentAiAssistImprove()"></button>
                        }
                      </div>
                    }
                    <ain-growth-content-asset-editor
                      [state]="articleEditorState"
                      (stateChange)="onArticleEditorStateChange($event)" />
                  }
                  @case ('submit_review') {
                    <p>{{ tw('steps.submit_review.summary') }}</p>
                    <dl class="summary-grid">
                      <div>
                        <dt>{{ tw('steps.content.fieldTitle') }}</dt>
                        <dd>{{ runContext('title') }}</dd>
                      </div>
                      <div>
                        <dt>{{ tw('steps.content.slug') }}</dt>
                        <dd>{{ runContext('publicSlug') }}</dd>
                      </div>
                    </dl>
                  }
                  @case ('approve_content') {
                    <p>{{ tw('steps.approve_content.summary') }}</p>
                    <p class="step-summary">{{ tw('steps.approve_content.status') }}: {{ runContext('contentStatus') }}</p>
                  }
                  @case ('publish_plan') {
                    <p>{{ tw('steps.publish_plan.summary') }}</p>
                    <ain-info-hint
                      [titleKey]="'wizards.growth.articlePublish.steps.publish_plan.channelsHelp.title'"
                      [descriptionKey]="'wizards.growth.articlePublish.steps.publish_plan.channelsHelp.description'"
                      [bulletKeys]="[
                        'wizards.growth.articlePublish.steps.publish_plan.channelsHelp.bullets.web',
                        'wizards.growth.articlePublish.steps.publish_plan.channelsHelp.bullets.messenger',
                        'wizards.growth.articlePublish.steps.publish_plan.channelsHelp.bullets.comms'
                      ]" />
                    <div class="channel-options">
                      <label class="channel-option">
                        <p-checkbox [binary]="true" [ngModel]="true" [disabled]="true" inputId="pub-web-blog" />
                        <span>{{ t('growth.publish.channels.web-blog') }}</span>
                      </label>
                      @for (option of articleOptionalPublishChannels; track option.value) {
                        <label class="channel-option" [attr.for]="'pub-' + option.value">
                          <p-checkbox
                            [binary]="true"
                            [ngModel]="isArticlePublishChannelSelected(option.value)"
                            (ngModelChange)="toggleArticlePublishChannel(option.value, $event)"
                            [inputId]="'pub-' + option.value"
                            [disabled]="isArticleCommsChannelDisabled(option.value)" />
                          <span>{{ option.label }}</span>
                        </label>
                      }
                      @if (!hasApprovedActivationSegment()) {
                        <p class="step-summary muted">{{ tw('steps.publish_plan.commsDisabledHint') }}</p>
                      }
                    </div>
                  }
                  @case ('plan_approval') {
                    <p>{{ tw('steps.plan_approval.summary') }}</p>
                    <p class="step-summary">{{ tw('steps.plan_approval.status') }}: {{ runContext('publishPlanStatus') }}</p>
                  }
                  @case ('execute') {
                    <p>{{ tw('steps.execute.summary') }}</p>
                    @if (runContext('publicSlug') !== '—') {
                      <dl class="summary-grid">
                        <div>
                          <dt>{{ tw('steps.execute.publicSlug') }}</dt>
                          <dd>{{ runContext('publicSlug') }}</dd>
                        </div>
                        @if (runContext('publicUrlPath') !== '—') {
                          <div>
                            <dt>{{ tw('steps.execute.publicUrl') }}</dt>
                            <dd><code>{{ runContext('publicUrlPath') }}</code></dd>
                          </div>
                        }
                      </dl>
                    }
                  }
                  @case ('done') {
                    <p>{{ tw('steps.done.intro') }}</p>
                    @if (runContext('publicSlug') !== '—') {
                      <dl class="summary-grid">
                        <div>
                          <dt>{{ tw('steps.done.publicSlug') }}</dt>
                          <dd>{{ runContext('publicSlug') }}</dd>
                        </div>
                        @if (runContext('publicUrlPath') !== '—') {
                          <div>
                            <dt>{{ tw('steps.done.publicUrl') }}</dt>
                            <dd><code>{{ runContext('publicUrlPath') }}</code></dd>
                          </div>
                        }
                      </dl>
                    }
                  }
                }
              } @else if (isSoftwareWizard()) {
                @switch (currentStepKey()) {
                  @case ('welcome') {
                    <p>{{ tw('steps.welcome.intro') }}</p>
                    <dl class="summary-grid">
                      <div>
                        <dt>{{ tw('steps.welcome.storeName') }}</dt>
                        <dd>{{ runContext('storeName') }}</dd>
                      </div>
                      <div>
                        <dt>{{ tw('steps.welcome.storeKey') }}</dt>
                        <dd>{{ runContext('storeKey') }}</dd>
                      </div>
                      <div>
                        <dt>{{ tw('steps.welcome.defaultCulture') }}</dt>
                        <dd>{{ runContext('defaultCulture') }}</dd>
                      </div>
                      <div>
                        <dt>{{ tw('steps.welcome.defaultCurrency') }}</dt>
                        <dd>{{ runContext('defaultCurrency') }}</dd>
                      </div>
                    </dl>
                  }
                  @case ('brand_profile') {
                    <div class="form-grid">
                      <label class="field">
                        <span>{{ tw('steps.brand_profile.brandName') }}</span>
                        <input pInputText [(ngModel)]="profileBrandName" />
                      </label>
                      <label class="field">
                        <span>{{ tw('steps.brand_profile.tagline') }}</span>
                        <input pInputText [(ngModel)]="profileTagline" />
                      </label>
                      <label class="field">
                        <span>{{ tw('steps.brand_profile.shortDescription') }}</span>
                        <textarea pTextarea [(ngModel)]="profileShortDescription" rows="3"></textarea>
                      </label>
                      <label class="field">
                        <span>{{ tw('steps.brand_profile.phones') }}</span>
                        @for (phone of profilePhones; track $index; let i = $index) {
                          <div class="inline-row">
                            <input pInputText [(ngModel)]="profilePhones[i]" />
                            <button pButton type="button" severity="secondary" [label]="tw('buttons.remove')" (click)="removePhone(i)"></button>
                          </div>
                        }
                        <button pButton type="button" severity="secondary" [label]="tw('buttons.addPhone')" (click)="addPhone()"></button>
                      </label>
                      <label class="field">
                        <span>{{ tw('steps.brand_profile.emails') }}</span>
                        @for (email of profileEmails; track $index; let i = $index) {
                          <div class="inline-row">
                            <input pInputText [(ngModel)]="profileEmails[i]" />
                            <button pButton type="button" severity="secondary" [label]="tw('buttons.remove')" (click)="removeEmail(i)"></button>
                          </div>
                        }
                        <button pButton type="button" severity="secondary" [label]="tw('buttons.addEmail')" (click)="addEmail()"></button>
                      </label>
                      <label class="field">
                        <span>{{ tw('steps.brand_profile.address') }}</span>
                        <input pInputText [(ngModel)]="profileAddress" />
                      </label>
                      <label class="field">
                        <span>{{ tw('steps.brand_profile.hours') }}</span>
                        <input pInputText [(ngModel)]="profileHours" />
                      </label>
                      <label class="field">
                        <span>{{ tw('steps.brand_profile.aboutHeadline') }}</span>
                        <input pInputText [(ngModel)]="profileAboutHeadline" />
                      </label>
                      <label class="field">
                        <span>{{ tw('steps.brand_profile.aboutBody') }}</span>
                        <textarea pTextarea [(ngModel)]="profileAboutBody" rows="4"></textarea>
                      </label>
                    </div>
                  }
                  @case ('catalog_starter') {
                    <label class="field">
                      <span>{{ tw('steps.catalogStarter.defaultCategoryName') }}</span>
                      <input pInputText [(ngModel)]="catalogDefaultCategoryName" />
                    </label>
                    @for (product of catalogProducts; track $index; let i = $index) {
                      <fieldset class="product-card">
                        <legend>{{ tw('steps.catalogStarter.productHeading') }} {{ i + 1 }}</legend>
                        <div class="form-grid">
                          <label class="field">
                            <span>{{ tw('steps.catalogStarter.title') }}</span>
                            <input pInputText [(ngModel)]="catalogProducts[i].title" />
                          </label>
                          <label class="field">
                            <span>{{ tw('steps.catalogStarter.categoryName') }}</span>
                            <input pInputText [(ngModel)]="catalogProducts[i].categoryName" />
                          </label>
                          <label class="field">
                            <span>{{ tw('steps.catalogStarter.shortDescription') }}</span>
                            <textarea pTextarea [(ngModel)]="catalogProducts[i].shortDescription" rows="2"></textarea>
                          </label>
                          <label class="field">
                            <span>{{ tw('steps.catalogStarter.basePrice') }}</span>
                            <input pInputText type="number" [(ngModel)]="catalogProducts[i].basePrice" />
                          </label>
                          @if (catalogProducts.length > 1) {
                            <button pButton type="button" severity="danger" [label]="tw('buttons.removeProduct')" (click)="removeProduct(i)"></button>
                          }
                        </div>
                      </fieldset>
                    }
                    <button pButton type="button" severity="secondary" [label]="tw('buttons.addProduct')" (click)="addProduct()"></button>
                  }
                  @case ('solutions_starter') {
                    <p>{{ tw('steps.solutions_starter.intro') }}</p>
                    <label class="field checkbox-field">
                      <p-checkbox [(ngModel)]="solutionSkip" [binary]="true" inputId="solutionSkip" />
                      <label for="solutionSkip">{{ tw('steps.solutions_starter.skip') }}</label>
                    </label>
                    @if (!solutionSkip) {
                      <div class="form-grid">
                        <label class="field">
                          <span>{{ tw('steps.solutions_starter.solutionTitle') }}</span>
                          <input pInputText [(ngModel)]="solutionTitle" />
                        </label>
                        <label class="field">
                          <span>{{ tw('steps.solutions_starter.shortDescription') }}</span>
                          <textarea pTextarea [(ngModel)]="solutionShortDescription" rows="3"></textarea>
                        </label>
                      </div>
                    }
                  }
                  @case ('content_starter') {
                    <p>{{ tw('steps.content_starter.intro') }}</p>
                    <p class="step-summary"><code>{{ runContext('contentAdminPath') }}</code></p>
                  }
                  @case ('assessments_hint') {
                    <p>{{ tw('steps.assessments_hint.intro') }}</p>
                    <p class="step-summary"><code>{{ runContext('assessmentsAdminPath') }}</code> · <code>{{ runContext('assessmentsPublicPath') }}</code></p>
                  }
                  @case ('engagement_forms') {
                    <p>{{ tw('steps.engagement_forms.intro') }}</p>
                    <ul class="step-summary">
                      <li><code>/contact</code></li>
                      <li><code>/book-demo</code></li>
                      <li><code>/schedule-meeting</code></li>
                      <li><code>{{ runContext('leadsAdminPath') }}</code></li>
                    </ul>
                  }
                  @case ('customers_ready') {
                    <p>{{ tw('steps.customers_ready.intro') }}</p>
                    <ul class="step-summary">
                      <li><code>{{ runContext('customersAdminPath') }}</code></li>
                      <li><code>{{ runContext('leadsAdminPath') }}</code></li>
                    </ul>
                  }
                  @case ('publish') {
                    <p>{{ tw('steps.publish.intro') }}</p>
                    <p class="step-summary">{{ tw('steps.publish.summary') }}: {{ contextIdCount('offeringIds', 'offeringId') }}</p>
                  }
                  @case ('done') {
                    <p>{{ tw('steps.done.intro') }}</p>
                    <ul class="step-summary">
                      <li><code>{{ runContext('publicProductsPath') }}</code></li>
                      <li><code>{{ runContext('publicSolutionsPath') }}</code></li>
                      <li><code>{{ runContext('publicBookDemoPath') }}</code></li>
                      <li><code>{{ runContext('publicScheduleMeetingPath') }}</code></li>
                      <li><code>{{ runContext('customersAdminPath') }}</code></li>
                      <li><code>{{ runContext('leadsAdminPath') }}</code></li>
                    </ul>
                  }
                }
              } @else {
              @switch (currentStepKey()) {
                @case ('welcome') {
                  <p>{{ t('wizards.marketplace.storeSetup.steps.welcome.intro') }}</p>
                  <dl class="summary-grid">
                    <div>
                      <dt>{{ t('wizards.marketplace.storeSetup.steps.welcome.storeName') }}</dt>
                      <dd>{{ runContext('storeName') }}</dd>
                    </div>
                    <div>
                      <dt>{{ t('wizards.marketplace.storeSetup.steps.welcome.storeKey') }}</dt>
                      <dd>{{ runContext('storeKey') }}</dd>
                    </div>
                    <div>
                      <dt>{{ t('wizards.marketplace.storeSetup.steps.welcome.defaultCulture') }}</dt>
                      <dd>{{ runContext('defaultCulture') }}</dd>
                    </div>
                    <div>
                      <dt>{{ t('wizards.marketplace.storeSetup.steps.welcome.defaultCurrency') }}</dt>
                      <dd>{{ runContext('defaultCurrency') }}</dd>
                    </div>
                  </dl>
                }
                @case ('store_mode') {
                  <div class="form-grid">
                    <label class="field checkbox-field">
                      <p-checkbox [(ngModel)]="marketplaceMode" [binary]="true" inputId="marketplaceMode" />
                      <label for="marketplaceMode">{{ t('wizards.marketplace.storeSetup.steps.storeMode.marketplaceMode') }}</label>
                    </label>
                    <label class="field">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.storeMode.storeName') }}</span>
                      <input pInputText [(ngModel)]="storeModeName" />
                    </label>
                    <label class="field">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.storeMode.defaultCulture') }}</span>
                      <input pInputText [(ngModel)]="storeModeCulture" />
                    </label>
                    <label class="field">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.storeMode.defaultCurrency') }}</span>
                      <input pInputText [(ngModel)]="storeModeCurrency" />
                    </label>
                    <label class="field checkbox-field">
                      <p-checkbox [(ngModel)]="enableSellerStorefronts" [binary]="true" inputId="enableSellerStorefronts" />
                      <label for="enableSellerStorefronts">{{ t('wizards.marketplace.storeSetup.steps.storeMode.enableSellerStorefronts') }}</label>
                    </label>
                    <label class="field checkbox-field">
                      <p-checkbox [(ngModel)]="enableCompare" [binary]="true" inputId="enableCompare" />
                      <label for="enableCompare">{{ t('wizards.marketplace.storeSetup.steps.storeMode.enableCompare') }}</label>
                    </label>
                    <label class="field">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.storeMode.heroLayout') }}</span>
                      <p-select
                        [options]="heroLayoutOptions"
                        [(ngModel)]="heroLayout"
                        optionLabel="label"
                        optionValue="value"
                        [placeholder]="t('wizards.marketplace.storeSetup.steps.storeMode.heroLayoutPlaceholder')"
                        [showClear]="true"
                        appendTo="body" />
                    </label>
                    <label class="field">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.storeMode.heroTransition') }}</span>
                      <p-select
                        [options]="heroTransitionOptions"
                        [(ngModel)]="heroTransition"
                        optionLabel="label"
                        optionValue="value"
                        [placeholder]="t('wizards.marketplace.storeSetup.steps.storeMode.heroTransitionPlaceholder')"
                        [showClear]="true"
                        appendTo="body" />
                    </label>
                  </div>
                }
                @case ('profile') {
                  <div class="profile-toolbar">
                    <button
                      pButton
                      type="button"
                      icon="pi pi-sparkles"
                      [label]="t('wizards.marketplace.storeSetup.buttons.aiFill')"
                      [disabled]="!profileStoreId()"
                      (click)="openAiFillDialog()"></button>
                  </div>
                  <div class="form-grid">
                    <label class="field">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.profile.brandName') }}</span>
                      <input pInputText [(ngModel)]="profileBrandName" />
                    </label>
                    <label class="field">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.profile.tagline') }}</span>
                      <input pInputText [(ngModel)]="profileTagline" />
                    </label>
                    <label class="field wide">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.profile.shortDescription') }}</span>
                      <textarea pTextarea [(ngModel)]="profileShortDescription" rows="2"></textarea>
                    </label>
                    <div class="field wide list-field">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.profile.phones') }}</span>
                      @for (phone of profilePhones; track $index; let i = $index) {
                        <div class="list-row">
                          <input pInputText [(ngModel)]="profilePhones[i]" />
                          @if (profilePhones.length > 1) {
                            <button pButton type="button" severity="secondary" [label]="t('wizards.marketplace.storeSetup.buttons.remove')" (click)="removePhone(i)"></button>
                          }
                        </div>
                      }
                      <button pButton type="button" severity="secondary" [label]="t('wizards.marketplace.storeSetup.buttons.addPhone')" (click)="addPhone()"></button>
                    </div>
                    <div class="field wide list-field">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.profile.emails') }}</span>
                      @for (email of profileEmails; track $index; let i = $index) {
                        <div class="list-row">
                          <input pInputText [(ngModel)]="profileEmails[i]" />
                          <button pButton type="button" severity="secondary" [label]="t('wizards.marketplace.storeSetup.buttons.remove')" (click)="removeEmail(i)"></button>
                        </div>
                      }
                      <button pButton type="button" severity="secondary" [label]="t('wizards.marketplace.storeSetup.buttons.addEmail')" (click)="addEmail()"></button>
                    </div>
                    <label class="field wide">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.profile.address') }}</span>
                      <textarea pTextarea [(ngModel)]="profileAddress" rows="2"></textarea>
                    </label>
                    <label class="field">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.profile.hours') }}</span>
                      <input pInputText [(ngModel)]="profileHours" />
                    </label>
                    <label class="field wide">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.profile.aboutHeadline') }}</span>
                      <input pInputText [(ngModel)]="profileAboutHeadline" />
                    </label>
                    <label class="field wide">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.profile.aboutBody') }}</span>
                      <textarea pTextarea [(ngModel)]="profileAboutBody" rows="4"></textarea>
                    </label>
                    <div class="field wide list-field">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.profile.aboutHighlights') }}</span>
                      @for (highlight of profileAboutHighlights; track $index; let i = $index) {
                        <div class="nested-card">
                          <label class="field">
                            <span>{{ t('wizards.marketplace.storeSetup.steps.profile.highlightTitle') }}</span>
                            <input pInputText [(ngModel)]="profileAboutHighlights[i].title" />
                          </label>
                          <label class="field">
                            <span>{{ t('wizards.marketplace.storeSetup.steps.profile.highlightBody') }}</span>
                            <textarea pTextarea [(ngModel)]="profileAboutHighlights[i].body" rows="2"></textarea>
                          </label>
                          <button pButton type="button" severity="secondary" [label]="t('wizards.marketplace.storeSetup.buttons.remove')" (click)="removeAboutHighlight(i)"></button>
                        </div>
                      }
                      <button pButton type="button" severity="secondary" [label]="t('wizards.marketplace.storeSetup.buttons.addHighlight')" (click)="addAboutHighlight()"></button>
                    </div>
                    <div class="field wide list-field">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.profile.faqItems') }}</span>
                      @for (faq of profileFaqItems; track $index; let i = $index) {
                        <div class="nested-card">
                          <label class="field">
                            <span>{{ t('wizards.marketplace.storeSetup.steps.profile.faqQuestion') }}</span>
                            <input pInputText [(ngModel)]="profileFaqItems[i].question" />
                          </label>
                          <label class="field">
                            <span>{{ t('wizards.marketplace.storeSetup.steps.profile.faqAnswer') }}</span>
                            <textarea pTextarea [(ngModel)]="profileFaqItems[i].answer" rows="2"></textarea>
                          </label>
                          <label class="field">
                            <span>{{ t('wizards.marketplace.storeSetup.steps.profile.faqCategory') }}</span>
                            <input pInputText [(ngModel)]="profileFaqItems[i].category" />
                          </label>
                          <button pButton type="button" severity="secondary" [label]="t('wizards.marketplace.storeSetup.buttons.remove')" (click)="removeFaqItem(i)"></button>
                        </div>
                      }
                      <button pButton type="button" severity="secondary" [label]="t('wizards.marketplace.storeSetup.buttons.addFaq')" (click)="addFaqItem()"></button>
                    </div>
                    <label class="field">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.profile.socialInstagram') }}</span>
                      <input pInputText [(ngModel)]="profileInstagram" />
                    </label>
                    <label class="field">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.profile.socialTelegram') }}</span>
                      <input pInputText [(ngModel)]="profileTelegram" />
                    </label>
                    <label class="field">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.profile.socialWebsite') }}</span>
                      <input pInputText [(ngModel)]="profileWebsite" />
                    </label>
                    <label class="field wide">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.profile.headerTopBarMessage') }}</span>
                      <input pInputText [(ngModel)]="profileHeaderTopBarMessage" />
                    </label>
                    <div class="field wide list-field">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.profile.headerQuickLinks') }}</span>
                      @for (link of profileHeaderQuickLinks; track $index; let i = $index) {
                        <div class="list-row">
                          <input pInputText [(ngModel)]="profileHeaderQuickLinks[i].label" [placeholder]="t('wizards.marketplace.storeSetup.steps.profile.linkLabel')" />
                          <input pInputText [(ngModel)]="profileHeaderQuickLinks[i].url" [placeholder]="t('wizards.marketplace.storeSetup.steps.profile.linkUrl')" />
                          <button pButton type="button" severity="secondary" [label]="t('wizards.marketplace.storeSetup.buttons.remove')" (click)="removeHeaderQuickLink(i)"></button>
                        </div>
                      }
                      <button pButton type="button" severity="secondary" [label]="t('wizards.marketplace.storeSetup.buttons.addHeaderLink')" (click)="addHeaderQuickLink()"></button>
                    </div>
                    <label class="field wide">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.profile.footerBrandText') }}</span>
                      <input pInputText [(ngModel)]="profileFooterBrandText" />
                    </label>
                    <div class="field wide list-field">
                      <span>{{ t('wizards.marketplace.storeSetup.steps.profile.footerQuickLinks') }}</span>
                      @for (link of profileFooterQuickLinks; track $index; let i = $index) {
                        <div class="list-row">
                          <input pInputText [(ngModel)]="profileFooterQuickLinks[i].label" [placeholder]="t('wizards.marketplace.storeSetup.steps.profile.linkLabel')" />
                          <input pInputText [(ngModel)]="profileFooterQuickLinks[i].url" [placeholder]="t('wizards.marketplace.storeSetup.steps.profile.linkUrl')" />
                          <button pButton type="button" severity="secondary" [label]="t('wizards.marketplace.storeSetup.buttons.remove')" (click)="removeFooterQuickLink(i)"></button>
                        </div>
                      }
                      <button pButton type="button" severity="secondary" [label]="t('wizards.marketplace.storeSetup.buttons.addFooterLink')" (click)="addFooterQuickLink()"></button>
                    </div>
                  </div>
                }
                @case ('catalog_starter') {
                  <label class="field">
                    <span>{{ t('wizards.marketplace.storeSetup.steps.catalogStarter.defaultCategoryName') }}</span>
                    <input pInputText [(ngModel)]="catalogDefaultCategoryName" />
                  </label>
                  @for (product of catalogProducts; track $index; let i = $index) {
                    <fieldset class="product-card">
                      <legend>{{ t('wizards.marketplace.storeSetup.steps.catalogStarter.productHeading') }} {{ i + 1 }}</legend>
                      <div class="form-grid">
                        <label class="field">
                          <span>{{ t('wizards.marketplace.storeSetup.steps.catalogStarter.title') }}</span>
                          <input pInputText [(ngModel)]="catalogProducts[i].title" />
                        </label>
                        <label class="field">
                          <span>{{ t('wizards.marketplace.storeSetup.steps.catalogStarter.categoryName') }}</span>
                          <input pInputText [(ngModel)]="catalogProducts[i].categoryName" />
                        </label>
                        <label class="field wide">
                          <span>{{ t('wizards.marketplace.storeSetup.steps.catalogStarter.shortDescription') }}</span>
                          <textarea pTextarea [(ngModel)]="catalogProducts[i].shortDescription" rows="2"></textarea>
                        </label>
                        <label class="field">
                          <span>{{ t('wizards.marketplace.storeSetup.steps.catalogStarter.basePrice') }}</span>
                          <p-inputNumber [(ngModel)]="catalogProducts[i].basePrice" [minFractionDigits]="0" [maxFractionDigits]="2" />
                        </label>
                      </div>
                      @if (catalogProducts.length > 1) {
                        <button pButton type="button" severity="danger" [label]="t('wizards.marketplace.storeSetup.buttons.removeProduct')" (click)="removeProduct(i)"></button>
                      }
                    </fieldset>
                  }
                  <button pButton type="button" severity="secondary" [label]="t('wizards.marketplace.storeSetup.buttons.addProduct')" (click)="addProduct()"></button>
                }
                @case ('storefront_nav') {
                  <p>{{ t('wizards.marketplace.storeSetup.steps.storefrontNav.intro') }}</p>
                  <p class="step-summary">{{ t('wizards.marketplace.storeSetup.steps.storefrontNav.summary') }}: {{ contextIdCount('categoryIds', 'categoryId') }}</p>
                  <div class="form-grid">
                    <label class="field checkbox-field">
                      <p-checkbox [(ngModel)]="navIsFeatured" [binary]="true" inputId="navIsFeatured" />
                      <label for="navIsFeatured">{{ t('wizards.marketplace.storeSetup.steps.storefrontNav.isFeatured') }}</label>
                    </label>
                    <label class="field checkbox-field">
                      <p-checkbox [(ngModel)]="navShowInNavigation" [binary]="true" inputId="navShowInNavigation" />
                      <label for="navShowInNavigation">{{ t('wizards.marketplace.storeSetup.steps.storefrontNav.showInNavigation') }}</label>
                    </label>
                  </div>
                }
                @case ('home') {
                  <p>{{ t('wizards.marketplace.storeSetup.steps.home.intro') }}</p>
                  <p class="step-summary">{{ t('wizards.marketplace.storeSetup.steps.home.summary') }}: {{ contextIdCount('offeringIds', 'offeringId') }}</p>
                }
                @case ('publish') {
                  <p>{{ t('wizards.marketplace.storeSetup.steps.publish.intro') }}</p>
                  <p class="step-summary">{{ t('wizards.marketplace.storeSetup.steps.publish.summary') }}: {{ contextIdCount('offeringIds', 'offeringId') }}</p>
                }
                @case ('done') {
                  <p>{{ t('wizards.marketplace.storeSetup.steps.done.intro') }}</p>
                }
              }
              }
            }

            @if (stepError()) {
              <ain-state type="error" [title]="tw('stepFailed')" [message]="stepError() ?? ''" />
            }

            @if (run()!.status === 'InProgress') {
              <div class="form-actions">
                @if (showAbandon()) {
                  <button
                    pButton
                    type="button"
                    severity="secondary"
                    [label]="tw('buttons.abandon')"
                    [disabled]="submitting()"
                    (click)="abandonWizard()"></button>
                }
                <button
                  pButton
                  type="button"
                  [label]="primaryButtonLabel()"
                  [loading]="submitting()"
                  [disabled]="submitting()"
                  (click)="submitCurrentStep()"></button>
              </div>
            }
          </section>
        }
      }

      <p-dialog
        [(visible)]="aiFillDialogVisible"
        [modal]="true"
        [draggable]="false"
        [header]="t('wizards.marketplace.storeSetup.ai.title')"
        [style]="{ width: 'min(560px, 96vw)' }"
        appendTo="body"
        (onHide)="resetAiFillDialog()">
        <div class="form-grid ai-dialog-grid">
          <label class="field wide">
            <span>{{ t('wizards.marketplace.storeSetup.ai.brandDescription') }}</span>
            <textarea pTextarea [(ngModel)]="aiBrandDescription" rows="4"></textarea>
          </label>
          <label class="field">
            <span>{{ t('wizards.marketplace.storeSetup.ai.industry') }}</span>
            <input pInputText [(ngModel)]="aiIndustry" />
          </label>
          <label class="field">
            <span>{{ t('wizards.marketplace.storeSetup.ai.audience') }}</span>
            <input pInputText [(ngModel)]="aiAudience" />
          </label>
          <label class="field">
            <span>{{ t('wizards.marketplace.storeSetup.ai.tone') }}</span>
            <input pInputText [(ngModel)]="aiTone" />
          </label>
          <label class="field">
            <span>{{ t('wizards.marketplace.storeSetup.ai.language') }}</span>
            <input pInputText [(ngModel)]="aiLanguage" />
          </label>
        </div>
        @if (aiFillError()) {
          <p class="ai-fill-error">{{ aiFillError() }}</p>
        }
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('wizards.marketplace.storeSetup.ai.cancel')" (click)="aiFillDialogVisible = false"></button>
          <button
            pButton
            type="button"
            [label]="t('wizards.marketplace.storeSetup.ai.generate')"
            [loading]="aiFillGenerating()"
            [disabled]="aiFillGenerating() || !aiBrandDescription.trim()"
            (click)="generateAiProfile()"></button>
        </ng-template>
      </p-dialog>

      @if (isGrowthWizard()) {
        <ain-ai-assist-shell
          #growthContentAiAssistShell
          [mode]="growthContentAiAssistShellMode()"
          [titleKey]="growthContentAiAssistTitleKey()"
          [infoTitleKey]="'growth.contentAsset.aiAssist.info.title'"
          [infoDescriptionKey]="'growth.contentAsset.aiAssist.info.description'"
          [infoBulletKeys]="[
            'growth.contentAsset.aiAssist.info.bullets.review',
            'growth.contentAsset.aiAssist.info.bullets.grounding'
          ]"
          [infoPreviewTitleKey]="'growth.contentAsset.aiAssist.preview.title'"
          [infoPreviewDescriptionKey]="'growth.contentAsset.aiAssist.preview.description'"
          [structuredFieldDefs]="growthContentAiAssistStructuredFields()"
          [showPromptTextarea]="growthContentAiAssistShowPrompt()"
          [requireInstruction]="growthContentAiAssistRequireInstruction()"
          [promptLabelKey]="'growth.contentAsset.aiAssist.prompt.label'"
          [promptHintKey]="'growth.contentAsset.aiAssist.prompt.hint'"
          [promptPlaceholderKey]="'growth.contentAsset.aiAssist.prompt.placeholder'"
          [permissionKey]="growthContentAiAssistPermissionKey"
          [generate]="generateGrowthContentAiAssist"
          [discard]="discardGrowthContentAiAssist"
          (applied)="onGrowthContentAiAssistApplied($event)">
          <div aiAssistContext class="ai-context-panel">
            <p class="ai-context-note">{{ t('growth.contentAsset.aiAssist.context.note') }}</p>
            <ain-form-field [label]="t('growth.contentAsset.aiAssist.context.knowledgeSource')">
              <ain-knowledge-source-selector [(ngModel)]="growthContentKnowledgeSourceId" statusFilter="Active" />
            </ain-form-field>
          </div>
        </ain-ai-assist-shell>
      }
    </ain-page>
  `,
  styles: `
    .checklist-panel { margin: 1.5rem 0; padding: 1rem 1.25rem; border: 1px solid var(--surface-border); border-radius: 8px; }
    .checklist { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.75rem; }
    .checklist li { display: flex; gap: 0.75rem; align-items: flex-start; }
    .mark { width: 1.5rem; text-align: center; flex-shrink: 0; }
    .step-body strong { display: block; }
    .step-error { margin: 0.25rem 0 0; color: var(--red-600); font-size: 0.9rem; }
    .step-panel { margin-top: 1.5rem; display: grid; gap: 1rem; }
    .field { display: grid; gap: 0.35rem; }
    .checkbox-field { display: flex; align-items: center; gap: 0.5rem; }
    .form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1rem; }
    .form-grid .wide { grid-column: span 2; }
    .list-field .list-row { display: flex; gap: 0.5rem; align-items: center; margin-bottom: 0.5rem; }
    .list-field .list-row input { flex: 1; }
    .product-card { border: 1px solid var(--surface-border); border-radius: 8px; padding: 1rem; margin: 0; display: grid; gap: 0.75rem; }
    .nested-card { border: 1px solid var(--surface-border); border-radius: 8px; padding: 0.75rem; display: grid; gap: 0.75rem; margin-bottom: 0.5rem; }
    .profile-toolbar { display: flex; justify-content: flex-end; margin-bottom: 0.25rem; }
    .ai-dialog-grid { margin-bottom: 0.5rem; }
    .ai-fill-error { margin: 0.5rem 0 0; color: var(--red-600); font-size: 0.9rem; }
    .summary-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem 1.5rem; margin: 0; }
    .summary-grid dt { font-size: 0.85rem; color: var(--text-color-secondary); margin: 0; }
    .summary-grid dd { margin: 0.15rem 0 0; font-weight: 600; }
    .step-summary { font-weight: 600; color: var(--primary-color); margin: 0; }
    .form-actions { margin-top: 0.5rem; }
    .success-panel { margin-top: 1.5rem; display: grid; gap: 1rem; }
    li.is-current .step-body strong { color: var(--primary-color); }
    .content-ai-toolbar { display: flex; justify-content: flex-end; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 0.5rem; }
    .ai-context-panel { display: grid; gap: 12px; }
    .ai-context-note { margin: 0; color: #475569; font-size: 0.9rem; line-height: 1.5; }
    .channel-options { display: grid; gap: 0.65rem; margin-top: 0.75rem; }
    .channel-option { display: flex; align-items: center; gap: 0.55rem; }
    .muted { color: var(--text-color-secondary); font-weight: 400; }
    @media (max-width: 760px) {
      .form-grid, .summary-grid { grid-template-columns: 1fr; }
      .form-grid .wide { grid-column: span 1; }
    }
  `
})
export class WizardRunHostComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly wizardApi = inject(WizardApiService);
  private readonly catalogService = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly growthMarketing = inject(GrowthMarketingService);
  private readonly growthDistribution = inject(GrowthDistributionService);
  private readonly contextService = inject(CurrentContextService);
  private readonly messages = inject(MessageService);

  private readonly growthContentAiAssistShell = viewChild<AinAiAssistShellComponent>('growthContentAiAssistShell');

  readonly growthContentAiAssistPermissionKey = GROWTH_CONTENT_AI_ASSIST_PERMISSION;

  readonly loading = signal(true);
  readonly submitting = signal(false);
  readonly error = signal<string | null>(null);
  readonly stepError = signal<string | null>(null);
  readonly run = signal<WizardRunDto | null>(null);

  marketplaceMode = false;
  storeModeName = '';
  storeModeCulture = 'fa-IR';
  storeModeCurrency = 'IRR';
  enableSellerStorefronts = true;
  enableCompare = false;
  heroLayout: string | null = null;
  heroTransition: string | null = null;

  get heroLayoutOptions(): SelectOption[] {
    return [
      { value: 'split', label: this.t('wizards.marketplace.storeSetup.steps.storeMode.heroLayoutOptions.split') },
      { value: 'banner', label: this.t('wizards.marketplace.storeSetup.steps.storeMode.heroLayoutOptions.banner') }
    ];
  }

  get heroTransitionOptions(): SelectOption[] {
    return [
      { value: 'fade', label: this.t('wizards.marketplace.storeSetup.steps.storeMode.heroTransitionOptions.fade') },
      { value: 'slide', label: this.t('wizards.marketplace.storeSetup.steps.storeMode.heroTransitionOptions.slide') }
    ];
  }

  profileBrandName = '';
  profileTagline = '';
  profileShortDescription = '';
  profilePhones: string[] = [''];
  profileEmails: string[] = [''];
  profileAddress = '';
  profileHours = '';
  profileAboutHeadline = '';
  profileAboutBody = '';
  profileInstagram = '';
  profileTelegram = '';
  profileWebsite = '';
  profileHeaderTopBarMessage = '';
  profileFooterBrandText = '';
  profileAboutHighlights: ProfileHighlightRow[] = [];
  profileFaqItems: ProfileFaqRow[] = [];
  profileHeaderQuickLinks: ProfileLinkRow[] = [];
  profileFooterQuickLinks: ProfileLinkRow[] = [];

  aiFillDialogVisible = false;
  aiBrandDescription = '';
  aiIndustry = '';
  aiAudience = '';
  aiTone = '';
  aiLanguage = 'fa-IR';
  readonly aiFillGenerating = signal(false);
  readonly aiFillError = signal<string | null>(null);

  catalogDefaultCategoryName = '';
  catalogProducts: CatalogProductRow[] = [{ title: '', shortDescription: '', categoryName: '', basePrice: null }];

  solutionTitle = '';
  solutionShortDescription = '';
  solutionSkip = false;

  navIsFeatured = true;
  navShowInNavigation = true;

  articleEditorState: GrowthContentAssetEditorState = createDefaultGrowthContentAssetEditorState();
  growthContentKnowledgeSourceId: string | null = null;
  selectedArticlePublishChannels: string[] = [GROWTH_ARTICLE_PUBLISH_CHANNEL];
  private hasApprovedActivationSegmentFlag = false;

  readonly articleOptionalPublishChannels = buildGrowthArticleOptionalPublishChannelOptions(key => this.t(key));

  readonly canUseGrowthContentAiAssist = computed(() =>
    hasAiAssistPermission(this.contextService.context(), GROWTH_CONTENT_AI_ASSIST_PERMISSION)
  );

  readonly growthContentAiAssistMode = computed((): 'create' | 'edit' => {
    const contentAssetId = this.run()?.context['contentAssetId']?.trim();
    return contentAssetId ? 'edit' : 'create';
  });

  readonly growthContentAiAssistIntent = signal<'generate' | 'improve'>('generate');

  readonly growthContentAiAssistShellMode = computed((): 'create' | 'edit' =>
    this.growthContentAiAssistIntent() === 'improve' ? 'edit' : this.growthContentAiAssistMode()
  );

  readonly growthContentAiAssistTitleKey = computed(() => {
    if (this.growthContentAiAssistIntent() === 'improve') {
      return 'growth.contentAsset.aiAssist.title.improve';
    }
    return this.growthContentAiAssistMode() === 'create'
      ? 'growth.contentAsset.aiAssist.title.create'
      : 'growth.contentAsset.aiAssist.title.edit';
  });

  readonly growthContentAiAssistShowPrompt = computed(
    () => this.growthContentAiAssistIntent() === 'improve' || this.growthContentAiAssistMode() === 'edit'
  );

  readonly growthContentAiAssistRequireInstruction = computed(
    () => this.growthContentAiAssistIntent() === 'improve' || this.growthContentAiAssistMode() === 'edit'
  );

  readonly generateGrowthContentAiAssist: AiAssistGenerateFn = payload => this.runGrowthContentAiAssistGenerate(payload);
  readonly discardGrowthContentAiAssist: AiAssistDiscardFn = draftId =>
    this.growthMarketing.discardContentAssistDraft(draftId);

  readonly isGrowthWizard = computed(
    () => this.run()?.definitionKey === 'growth.article.publish'
  );

  readonly isSoftwareWizard = computed(
    () => this.run()?.definitionKey === 'software.site.setup'
  );

  readonly i18nRoot = computed(() => {
    if (this.isGrowthWizard()) {
      return 'wizards.growth.articlePublish';
    }
    if (this.isSoftwareWizard()) {
      return 'wizards.software.siteSetup';
    }
    return 'wizards.marketplace.storeSetup';
  });

  readonly currentStepKey = computed(() => this.run()?.currentStepKey ?? null);

  readonly currentStepStatus = computed(() => {
    const run = this.run();
    const stepKey = this.currentStepKey();
    if (!run || !stepKey) {
      return null;
    }

    const result = run.stepResults.find(item => item.key === stepKey);
    return result?.status ?? 'Pending';
  });

  readonly checklist = computed((): ChecklistItem[] => {
    const run = this.run();
    if (!run) {
      return [];
    }

    const results = new Map(run.stepResults.map(item => [item.key, item]));
    return run.steps.map(step => {
      const result = results.get(step.key);
      return {
        key: step.key,
        status: result?.status ?? 'Pending',
        error: result?.error ?? null
      };
    });
  });

  readonly hintTitleKey = computed(() => `${this.i18nRoot()}.hints.${this.currentStepKey() ?? 'default'}.title`);
  readonly hintDescriptionKey = computed(() => `${this.i18nRoot()}.hints.${this.currentStepKey() ?? 'default'}.description`);
  readonly hintBulletKeys = computed(() => [
    `${this.i18nRoot()}.hints.${this.currentStepKey() ?? 'default'}.bullet1`,
    `${this.i18nRoot()}.hints.${this.currentStepKey() ?? 'default'}.bullet2`
  ]);

  constructor() {
    void this.loadRun();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  tw(key: string): string {
    return this.t(`${this.i18nRoot()}.${key}`);
  }

  stepLabel(stepKey: string): string {
    return this.tw(`steps.${stepKey}.title`);
  }

  stepStatusLabel(status: string): string {
    return this.tw(`status.${status}`);
  }

  showAbandon(): boolean {
    return this.isGrowthWizard() && this.run()?.status === 'InProgress';
  }

  completionButtonLabel(): string {
    if (this.isGrowthWizard()) {
      return this.tw('goToArticlePublishing');
    }
    if (this.isSoftwareWizard()) {
      return this.tw('goToStorefrontOps');
    }
    return this.tw('goToStores');
  }

  stepClass(step: ChecklistItem): string {
    const current = this.run()?.currentStepKey;
    return step.key === current && step.status === 'Pending' ? 'is-current' : '';
  }

  showCurrentStepForm(): boolean {
    return this.currentStepStatus() !== 'Skipped';
  }

  runContext(key: string): string {
    const run = this.run();
    if (!run?.context[key]) {
      return '—';
    }
    return run.context[key];
  }

  contextIdCount(listKey: string, singleKey: string): number {
    const run = this.run();
    if (!run) {
      return 0;
    }

    const listRaw = run.context[listKey];
    if (listRaw) {
      return listRaw.split(',').filter(item => item.trim().length > 0).length;
    }

    return run.context[singleKey] ? 1 : 0;
  }

  addPhone(): void {
    this.profilePhones = [...this.profilePhones, ''];
  }

  removePhone(index: number): void {
    this.profilePhones = this.profilePhones.filter((_, i) => i !== index);
    if (this.profilePhones.length === 0) {
      this.profilePhones = [''];
    }
  }

  addEmail(): void {
    this.profileEmails = [...this.profileEmails, ''];
  }

  removeEmail(index: number): void {
    this.profileEmails = this.profileEmails.filter((_, i) => i !== index);
    if (this.profileEmails.length === 0) {
      this.profileEmails = [''];
    }
  }

  profileStoreId(): string | null {
    const run = this.run();
    if (!run) {
      return null;
    }

    const contextStoreId = run.context['storeId']?.trim();
    if (contextStoreId) {
      return contextStoreId;
    }

    const subjectId = run.subjectId?.trim();
    return subjectId || null;
  }

  openAiFillDialog(): void {
    this.aiFillError.set(null);
    this.aiFillDialogVisible = true;
  }

  resetAiFillDialog(): void {
    this.aiFillError.set(null);
    this.aiFillGenerating.set(false);
  }

  async generateAiProfile(): Promise<void> {
    const storeId = this.profileStoreId();
    if (!storeId || !this.aiBrandDescription.trim()) {
      return;
    }

    this.aiFillGenerating.set(true);
    this.aiFillError.set(null);
    try {
      const result = await firstValueFrom(
        this.catalogService.generateStoreProfileDraft(storeId, {
          brandDescription: this.aiBrandDescription.trim(),
          industry: this.aiIndustry.trim() || null,
          audience: this.aiAudience.trim() || null,
          tone: this.aiTone.trim() || null,
          language: this.aiLanguage.trim() || null
        })
      );
      this.applyProfileFromAi(result.profile);
      this.aiFillDialogVisible = false;
    } catch (err) {
      this.aiFillError.set(this.formatError(err));
    } finally {
      this.aiFillGenerating.set(false);
    }
  }

  addAboutHighlight(): void {
    this.profileAboutHighlights = [...this.profileAboutHighlights, { title: '', body: '' }];
  }

  removeAboutHighlight(index: number): void {
    this.profileAboutHighlights = this.profileAboutHighlights.filter((_, i) => i !== index);
  }

  addFaqItem(): void {
    this.profileFaqItems = [...this.profileFaqItems, { question: '', answer: '', category: '' }];
  }

  removeFaqItem(index: number): void {
    this.profileFaqItems = this.profileFaqItems.filter((_, i) => i !== index);
  }

  addHeaderQuickLink(): void {
    this.profileHeaderQuickLinks = [...this.profileHeaderQuickLinks, { label: '', url: '' }];
  }

  removeHeaderQuickLink(index: number): void {
    this.profileHeaderQuickLinks = this.profileHeaderQuickLinks.filter((_, i) => i !== index);
  }

  addFooterQuickLink(): void {
    this.profileFooterQuickLinks = [...this.profileFooterQuickLinks, { label: '', url: '' }];
  }

  removeFooterQuickLink(index: number): void {
    this.profileFooterQuickLinks = this.profileFooterQuickLinks.filter((_, i) => i !== index);
  }

  addProduct(): void {
    this.catalogProducts = [
      ...this.catalogProducts,
      { title: '', shortDescription: '', categoryName: '', basePrice: null }
    ];
  }

  removeProduct(index: number): void {
    this.catalogProducts = this.catalogProducts.filter((_, i) => i !== index);
    if (this.catalogProducts.length === 0) {
      this.catalogProducts = [{ title: '', shortDescription: '', categoryName: '', basePrice: null }];
    }
  }

  primaryButtonLabel(): string {
    if (this.currentStepStatus() === 'AwaitingApproval') {
      return this.tw('buttons.resubmit');
    }

    const key = this.currentStepKey();
    if (key === 'done') {
      return this.tw('buttons.finish');
    }

    return this.tw('buttons.continue');
  }

  async loadRun(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    const runId = this.route.snapshot.paramMap.get('runId');
    if (!runId) {
      this.error.set('run_not_found');
      this.loading.set(false);
      return;
    }

    try {
      const run = await firstValueFrom(this.wizardApi.getRun(runId));
      this.run.set(run);
      this.seedFormFromContext(run);
    } catch (err) {
      this.error.set(this.formatError(err));
    } finally {
      this.loading.set(false);
    }
  }

  async submitCurrentStep(): Promise<void> {
    const run = this.run();
    const stepKey = this.currentStepKey();
    if (!run || !stepKey) {
      return;
    }

    this.submitting.set(true);
    this.stepError.set(null);
    try {
      const payload = this.buildPayload(stepKey);
      const updated = await firstValueFrom(this.wizardApi.submitStep(run.id, stepKey, JSON.stringify(payload)));
      this.run.set(updated);
      this.seedFormFromContext(updated);
      const failedStep = updated.stepResults.find(item => item.key === stepKey && item.status === 'Failed');
      if (failedStep?.error) {
        this.stepError.set(this.wizardErrorLabel(failedStep.error));
      }
      if (updated.status === 'Completed') {
        await this.goToCompletion();
      }
    } catch (err) {
      this.stepError.set(this.formatError(err));
    } finally {
      this.submitting.set(false);
    }
  }

  async goToCompletion(): Promise<void> {
    if (this.isGrowthWizard()) {
      await this.router.navigate(['/growth/marketing/article-publishing']);
      return;
    }

    if (this.isSoftwareWizard()) {
      await this.router.navigate(['/marketplace/storefront/publishing']);
      return;
    }

    await this.router.navigate(['/marketplace/storefront/stores']);
  }

  async abandonWizard(): Promise<void> {
    const run = this.run();
    if (!run) {
      return;
    }

    this.submitting.set(true);
    this.stepError.set(null);
    try {
      await firstValueFrom(this.wizardApi.abandonRun(run.id));
      await this.router.navigate(['/growth/marketing/article-publishing']);
    } catch (err) {
      this.stepError.set(this.formatError(err));
    } finally {
      this.submitting.set(false);
    }
  }

  private buildPayload(stepKey: string): unknown {
    if (this.isGrowthWizard()) {
      switch (stepKey) {
        case 'content':
          return buildGrowthArticlePublishContentStepPayload(this.articleEditorState);
        case 'publish_plan':
          return {
            selectedChannels: this.normalizeSelectedArticlePublishChannels()
          };
        default:
          return {};
      }
    }

    switch (stepKey) {
      case 'store_mode':
        return {
          marketplaceMode: this.marketplaceMode,
          storeName: this.storeModeName.trim() || null,
          defaultCulture: this.storeModeCulture.trim() || null,
          defaultCurrency: this.storeModeCurrency.trim() || null,
          enableSellerStorefronts: this.enableSellerStorefronts,
          enableCompare: this.enableCompare,
          heroLayout: this.heroLayout?.trim() || null,
          heroTransition: this.heroTransition?.trim() || null
        };
      case 'profile':
      case 'brand_profile':
        return {
          brandName: this.profileBrandName.trim(),
          tagline: this.profileTagline.trim() || null,
          shortDescription: this.profileShortDescription.trim() || null,
          phones: this.profilePhones.map(item => item.trim()).filter(item => item.length > 0),
          emails: this.profileEmails.map(item => item.trim()).filter(item => item.length > 0),
          address: this.profileAddress.trim() || null,
          hours: this.profileHours.trim() || null,
          aboutHeadline: this.profileAboutHeadline.trim() || null,
          aboutBody: this.profileAboutBody.trim() || null,
          social: {
            instagram: this.profileInstagram.trim() || null,
            telegram: this.profileTelegram.trim() || null,
            website: this.profileWebsite.trim() || null
          },
          headerTopBarMessage: this.profileHeaderTopBarMessage.trim() || null,
          footerBrandText: this.profileFooterBrandText.trim() || null,
          aboutHighlights: this.buildHighlightPayload(),
          faqItems: this.buildFaqPayload(),
          headerQuickLinks: this.buildLinkPayload(this.profileHeaderQuickLinks),
          footerQuickLinks: this.buildLinkPayload(this.profileFooterQuickLinks)
        };
      case 'catalog_starter':
        return {
          defaultCategoryName: this.catalogDefaultCategoryName.trim() || null,
          products: this.catalogProducts
            .map(item => ({
              title: item.title.trim(),
              shortDescription: item.shortDescription.trim() || null,
              categoryName: item.categoryName.trim() || null,
              basePrice: item.basePrice
            }))
            .filter(item => item.title.length > 0)
        };
      case 'solutions_starter':
        return {
          skip: this.solutionSkip,
          title: this.solutionSkip ? null : this.solutionTitle.trim() || null,
          shortDescription: this.solutionSkip ? null : this.solutionShortDescription.trim() || null
        };
      case 'storefront_nav':
        return {
          isFeatured: this.navIsFeatured,
          showInNavigation: this.navShowInNavigation
        };
      default:
        return {};
    }
  }

  private applyProfileFromAi(profile: StorefrontContentProfile): void {
    this.profileBrandName = profile.brandName?.trim() ?? '';
    this.profileTagline = profile.tagline?.trim() ?? '';
    this.profileShortDescription = profile.shortDescription?.trim() ?? '';
    this.profilePhones = profile.phones?.length ? [...profile.phones] : [''];
    this.profileEmails = profile.emails?.length ? [...profile.emails] : [''];
    this.profileAddress = profile.address?.trim() ?? '';
    this.profileHours = profile.hours?.trim() ?? '';
    this.profileAboutHeadline = profile.aboutHeadline?.trim() ?? '';
    this.profileAboutBody = profile.aboutBody?.trim() ?? '';
    this.profileInstagram = profile.socialLinks?.instagram?.trim() ?? '';
    this.profileTelegram = profile.socialLinks?.telegram?.trim() ?? '';
    this.profileWebsite = profile.socialLinks?.website?.trim() ?? '';
    this.profileHeaderTopBarMessage = profile.headerTopBarMessage?.trim() ?? '';
    this.profileFooterBrandText = profile.footerBrandText?.trim() ?? '';
    this.profileAboutHighlights = (profile.aboutHighlights ?? []).map(item => ({
      title: item.title?.trim() ?? '',
      body: item.body?.trim() ?? ''
    }));
    this.profileFaqItems = (profile.faqItems ?? []).map(item => ({
      question: item.question?.trim() ?? '',
      answer: item.answer?.trim() ?? '',
      category: item.category?.trim() ?? ''
    }));
    this.profileHeaderQuickLinks = (profile.headerQuickLinks ?? []).map(item => ({
      label: item.label?.trim() ?? '',
      url: item.url?.trim() ?? ''
    }));
    this.profileFooterQuickLinks = (profile.footerQuickLinks ?? []).map(item => ({
      label: item.label?.trim() ?? '',
      url: item.url?.trim() ?? ''
    }));
  }

  private buildHighlightPayload(): StorefrontProfileHighlight[] {
    return this.profileAboutHighlights
      .map(item => ({
        title: item.title.trim(),
        body: item.body.trim()
      }))
      .filter(item => item.title.length > 0 || item.body.length > 0);
  }

  private buildFaqPayload(): StorefrontProfileFaqItem[] {
    return this.profileFaqItems
      .map(item => ({
        question: item.question.trim(),
        answer: item.answer.trim(),
        category: item.category.trim() || null
      }))
      .filter(item => item.question.length > 0 || item.answer.length > 0);
  }

  private buildLinkPayload(links: ProfileLinkRow[]): StorefrontProfileLink[] {
    return links
      .map(item => ({
        label: item.label.trim(),
        url: item.url.trim()
      }))
      .filter(item => item.label.length > 0 || item.url.length > 0);
  }

  private seedFormFromContext(run: WizardRunDto): void {
    const storeName = run.context['storeName'];
    if (storeName) {
      if (!this.storeModeName) {
        this.storeModeName = storeName;
      }
      if (!this.profileBrandName) {
        this.profileBrandName = storeName;
      }
      if (this.catalogProducts.length === 1 && !this.catalogProducts[0].title) {
        this.catalogProducts[0].title = storeName;
      }
    }

    if (run.context['defaultCulture'] && !this.storeModeCulture) {
      this.storeModeCulture = run.context['defaultCulture'];
    }

    if (run.context['defaultCurrency'] && !this.storeModeCurrency) {
      this.storeModeCurrency = run.context['defaultCurrency'];
    }

    if (run.context['marketplaceMode'] === 'true') {
      this.marketplaceMode = true;
    } else if (run.context['marketplaceMode'] === 'false') {
      this.marketplaceMode = false;
    }

    if (run.definitionKey === 'growth.article.publish') {
      this.articleEditorState = patchGrowthContentAssetEditorFromWizardContext(this.articleEditorState, run.context);
      this.hydrateArticlePublishChannels(run.context);
      this.refreshApprovedActivationSegmentAvailability();
    }
  }

  isArticlePublishChannelSelected(channel: string): boolean {
    return this.selectedArticlePublishChannels.includes(channel);
  }

  toggleArticlePublishChannel(channel: string, checked: boolean): void {
    if (channel === GROWTH_ARTICLE_PUBLISH_CHANNEL) {
      return;
    }

    if (checked) {
      if (!this.selectedArticlePublishChannels.includes(channel)) {
        this.selectedArticlePublishChannels = [...this.selectedArticlePublishChannels, channel];
      }
      return;
    }

    this.selectedArticlePublishChannels = this.selectedArticlePublishChannels.filter(item => item !== channel);
  }

  isArticleCommsChannelDisabled(channel: string): boolean {
    return (channel === 'email' || channel === 'sms') && !this.hasApprovedActivationSegmentFlag;
  }

  hasApprovedActivationSegment(): boolean {
    return this.hasApprovedActivationSegmentFlag;
  }

  private normalizeSelectedArticlePublishChannels(): string[] {
    return Array.from(
      new Set(
        [GROWTH_ARTICLE_PUBLISH_CHANNEL, ...this.selectedArticlePublishChannels]
          .map(item => item.trim().toLowerCase())
          .filter(item => !!item)
      )
    );
  }

  private hydrateArticlePublishChannels(context: Record<string, string>): void {
    const fromContext = this.parseSelectedChannelsJson(context['selectedChannelsJson']);
    if (fromContext.length) {
      this.selectedArticlePublishChannels = fromContext;
      return;
    }

    const fromTargets = this.articleEditorState.publishTargetRows
      .map(row => row.channelKey?.trim().toLowerCase())
      .filter((item): item is string => !!item);
    this.selectedArticlePublishChannels = Array.from(
      new Set([GROWTH_ARTICLE_PUBLISH_CHANNEL, ...fromTargets.filter(item =>
        (GROWTH_ARTICLE_OPTIONAL_PUBLISH_CHANNELS as readonly string[]).includes(item) || item === GROWTH_ARTICLE_PUBLISH_CHANNEL
      )])
    );
  }

  private parseSelectedChannelsJson(raw: string | undefined): string[] {
    if (!raw?.trim()) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed
        .map(item => (typeof item === 'string' ? item.trim().toLowerCase() : ''))
        .filter(item => !!item);
    } catch {
      return [];
    }
  }

  private refreshApprovedActivationSegmentAvailability(): void {
    this.growthDistribution.listActivationPlans().subscribe({
      next: plans => {
        this.hasApprovedActivationSegmentFlag = plans.some(plan => {
          if ((plan.status ?? '').toLowerCase() !== 'approved') {
            return false;
          }
          try {
            const parsed = JSON.parse(plan.segmentRefsJson || '[]') as unknown;
            if (!Array.isArray(parsed) || !parsed.length) {
              return false;
            }
            return parsed.some(item => {
              if (!item || typeof item !== 'object') {
                return false;
              }
              const record = item as Record<string, unknown>;
              const id = record['ref'] ?? record['partySegmentRef'] ?? record['segmentId'] ?? record['id'];
              return typeof id === 'string' ? id.trim().length > 0 : typeof id === 'number';
            });
          } catch {
            return false;
          }
        });

        if (!this.hasApprovedActivationSegmentFlag) {
          this.selectedArticlePublishChannels = this.selectedArticlePublishChannels.filter(
            item => item !== 'email' && item !== 'sms'
          );
        }
      },
      error: () => {
        this.hasApprovedActivationSegmentFlag = false;
        this.selectedArticlePublishChannels = this.selectedArticlePublishChannels.filter(
          item => item !== 'email' && item !== 'sms'
        );
      }
    });
  }

  onArticleEditorStateChange(state: GrowthContentAssetEditorState): void {
    this.articleEditorState = state;
  }

  growthContentAiAssistStructuredFields(): StructuredJsonFieldDef[] {
    if (this.growthContentAiAssistIntent() === 'improve' || this.growthContentAiAssistMode() === 'edit') {
      return [];
    }

    return [
      { key: 'topic', type: 'string', labelKey: 'growth.contentAsset.aiAssist.fields.topic', required: true },
      { key: 'goal', type: 'string', labelKey: 'growth.contentAsset.aiAssist.fields.goal' },
      { key: 'audience', type: 'string', labelKey: 'growth.contentAsset.aiAssist.fields.audience' },
      { key: 'tone', type: 'string', labelKey: 'growth.contentAsset.aiAssist.fields.tone' },
      {
        key: 'length',
        type: 'select',
        labelKey: 'growth.contentAsset.aiAssist.fields.length',
        options: [
          { value: 'short', labelKey: 'growth.contentAsset.aiAssist.length.short' },
          { value: 'medium', labelKey: 'growth.contentAsset.aiAssist.length.medium' },
          { value: 'long', labelKey: 'growth.contentAsset.aiAssist.length.long' }
        ]
      },
      { key: 'keywords', type: 'string', labelKey: 'growth.contentAsset.aiAssist.fields.keywords' },
      { key: 'cta', type: 'string', labelKey: 'growth.contentAsset.aiAssist.fields.cta' },
      { key: 'constraints', type: 'textarea', labelKey: 'growth.contentAsset.aiAssist.fields.constraints' },
      {
        key: 'bodyFormat',
        type: 'select',
        labelKey: 'growth.contentAsset.aiAssist.fields.bodyFormat',
        options: [
          { value: 'markdown', labelKey: 'growth.contentAsset.fields.bodyFormat.markdown' },
          { value: 'html', labelKey: 'growth.contentAsset.fields.bodyFormat.html' }
        ]
      }
    ];
  }

  hasGrowthContentBodyForImprove(): boolean {
    return (this.articleEditorState.model.bodyMarkdown ?? '').trim().length > 0;
  }

  openGrowthContentAiAssist(): void {
    this.growthContentAiAssistIntent.set('generate');
    this.growthContentAiAssistShell()?.open({
      instruction: '',
      structuredAnswers:
        this.growthContentAiAssistMode() === 'create'
          ? {
              topic: this.articleEditorState.model.title.trim() || '',
              goal: '',
              audience: '',
              tone: '',
              length: 'medium',
              keywords: '',
              cta: '',
              constraints: '',
              bodyFormat: this.articleEditorState.model.bodyFormat
            }
          : {},
      contextSelections: this.buildGrowthContentAiAssistContextSelections()
    });
  }

  openGrowthContentAiAssistImprove(): void {
    this.growthContentAiAssistIntent.set('improve');
    this.growthContentAiAssistShell()?.open({
      instruction: this.t('growth.contentAsset.aiAssist.improve.defaultInstruction'),
      structuredAnswers: {
        length: 'long',
        bodyFormat: this.articleEditorState.model.bodyFormat
      },
      contextSelections: this.buildGrowthContentAiAssistContextSelections()
    });
  }

  onGrowthContentAiAssistApplied(event: AiAssistAppliedEvent): void {
    this.articleEditorState = applyGrowthContentAssistFieldPatches(this.articleEditorState, event.patches);

    if (event.result.usedFallback) {
      this.messages.add({
        severity: 'warn',
        summary: this.t('growth.contentAsset.aiAssist.fallbackApplied')
      });
    }

    const warnings = event.result.warnings ?? [];
    const coverUnavailable = warnings.some(item =>
      item.includes('growth_content_assist_cover_unavailable')
    );
    if (coverUnavailable) {
      this.messages.add({
        severity: 'warn',
        summary: this.t('growth.contentAsset.aiAssist.coverUnavailable.title'),
        detail: this.t('growth.contentAsset.aiAssist.coverUnavailable.detail'),
        life: 12000
      });
    }

    const bodyTooShort = warnings.some(
      item =>
        item.includes('growth_content_assist_body_too_short') ||
        item.toLowerCase().includes('shorter than the requested length')
    );
    if (bodyTooShort) {
      this.messages.add({
        severity: 'warn',
        summary: this.t('growth.contentAsset.aiAssist.bodyTooShort.title'),
        detail: this.t('growth.contentAsset.aiAssist.bodyTooShort.detail'),
        life: 12000
      });
    } else if (
      warnings.some(
        item =>
          !item.includes('growth_content_assist_cover_unavailable') &&
          !item.toLowerCase().includes('cover image could not be generated')
      )
    ) {
      this.messages.add({
        severity: 'info',
        summary: this.t('growth.contentAsset.aiAssist.appliedWithWarnings'),
        detail: warnings.slice(0, 3).join(' · '),
        life: 8000
      });
    }
  }

  private buildGrowthContentAiAssistContextSelections() {
    return {
      strategyArtifactIds: null,
      knowledgeSourceIds: this.growthContentKnowledgeSourceId ? [this.growthContentKnowledgeSourceId] : null,
      briefId: this.run()?.context['briefId']?.trim() || null,
      offeringId: null,
      segmentId: null,
      storeKey: null
    };
  }

  private runGrowthContentAiAssistGenerate(payload: AiAssistGenerateRequest): Observable<AiAssistResult> {
    const snapshot = buildGrowthContentAssistClientSnapshot(this.articleEditorState);
    const run = this.run();
    const contentAssetId = run?.context['contentAssetId']?.trim() || null;
    const isTransform = Boolean(contentAssetId);
    const versionRaw = run?.context['contentVersion']?.trim();
    const baseVersion = isTransform && versionRaw ? Number.parseInt(versionRaw, 10) : null;
    const structuredAnswersJson = JSON.stringify(payload.structuredAnswers ?? {});
    const bodyFormatFromAnswers =
      typeof payload.structuredAnswers?.['bodyFormat'] === 'string'
        ? String(payload.structuredAnswers['bodyFormat'])
        : this.articleEditorState.model.bodyFormat;

    return from(computeGrowthContentAssistSourceHash(snapshot, baseVersion)).pipe(
      switchMap(sourceHash => {
        const common = {
          actionKey: isTransform
            ? 'growth.content.asset.transform-draft'
            : 'growth.content.asset.generate-draft',
          instruction: payload.instruction || null,
          structuredAnswersJson,
          contextSelections: this.buildGrowthContentAiAssistContextSelections(),
          clientSnapshot: snapshot,
          sourceHash,
          fieldScopes: isTransform ? ['title', 'summary', 'body', 'seo'] : null,
          bodyFormat: bodyFormatFromAnswers,
          applyMode: 'ReturnDraftOnly',
          idempotencyKey: crypto.randomUUID(),
          correlationId: crypto.randomUUID(),
          invocationSource: 'AdminUi'
        };

        const request$ = isTransform
          ? this.growthMarketing.transformContentAssistDraft(contentAssetId!, { ...common, baseVersion })
          : this.growthMarketing.generateContentAssistDraft(common);

        return request$.pipe(
          map(response => ({
            draftId: response.draftId,
            patches: mapGrowthContentAssistDraftToFieldPatches(
              response,
              snapshot,
              this.articleEditorState.model.slug ?? '',
              this.articleEditorState.seo
            ),
            warnings: response.warnings ?? [],
            usedFallback: !!response.usedFallback,
            correlationId: response.correlationId,
            sourceHash: response.sourceHash
          }))
        );
      })
    );
  }

  private formatError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error as { Error?: string; Message?: string; error?: string } | null;
      const code = body?.error ?? body?.Error ?? body?.Message ?? err.message;
      if (typeof code === 'string' && code.startsWith('growth_content_assist_')) {
        const key = `shared.aiAssist.errors.${code}`;
        const translated = this.t(key);
        if (translated !== key) {
          return translated;
        }
      }
      return code;
    }

    return err instanceof Error ? err.message : 'unknown_error';
  }

  wizardErrorLabel(code: string): string {
    const key = `${this.i18nRoot()}.errors.${code}`;
    const translated = this.t(key);
    return translated !== key ? translated : code;
  }
}
